import { PriceChangeAlert } from '@/components/products/price-changes/PriceChangeAlert'
import AccountStatus from '@/components/dashboard/AccountStatus'
import Analytics from '@/components/dashboard/Analytics'
import SalesDemoBanner from '@/components/sales-demos/SalesDemoBanner'
import SeasonalStatusSummary from '@/components/seasonal/SeasonalStatusSummary'
import { CondolencesReconnectNotice } from '@/components/integrations/condolences/CondolencesReconnectNotice'
import IfGlobalOrg from '@/components/organizations/IfGlobalOrg'
import { InfoNotice, InfoNoticeAction } from '@/components/patterns/info-notice'
import StripeConnectAlertsPanel from '@/components/stripe-connect/StripeConnectAlertsPanel'
import { canShowStripeConnectBanner } from '@/components/stripe-connect/stripeConnectStatus'
import { Badge } from '@/components/ui/badge'
import { StyledIcon } from '@/components/ui/styled-icon'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import { useOrganizationContext } from '@/hooks/useOrganizationContext'
import { usePathHelper } from '@/hooks/usePathHelper'
import { useAbility } from '@/lib/casl'
import dayjs from '@/lib/dayjs'
import { SalesOrderFulfillmentStatus } from '@/lib/enums/SalesOrderFulfillmentStatus'
import { getCurrentReportDateFilters } from '@/lib/report-date-range'
import {
  fsnNetworkQueries,
  useFsnIncomingOrders,
} from '@/lib/services/fsn-network/fsn-network.query'
import { messageQueries } from '@/lib/services/messages/messages.query'
import { organizationQueries } from '@/lib/services/organizations/organizations.query'
import {
  availabilityChangeQueries,
  supportsAvailabilityChangesForOrganizationId,
  useAvailabilityChangeCurrentSummary,
  useAvailabilityChanges,
} from '@/lib/services/availability-changes/availability-changes.query'
import {
  formatUpcomingChange,
  getActiveAvailabilityChanges,
  getAvailabilityAlertTitle,
  getAvailabilityAlertTone,
  getAvailabilityDashboardDescription,
  getAvailabilityState,
  getUpcomingAvailabilityChangeOccurrences,
} from '@/lib/services/availability-changes/availability-changes.summary'
import { platformLedgerQueries } from '@/lib/services/platform-ledger/platform-ledger.query'
import {
  priceAdjustmentQueries,
  supportsPriceAdjustmentsForOrganizationId,
  useOptionalPriceAdjustments,
} from '@/lib/services/price-adjustments/priceAdjustments.query'
import {
  flowerSubscriptionQueries,
  useFlowerSubscriptions,
} from '@/lib/services/flower-subscriptions/flower-subscriptions.query'
import {
  getPriceAdjustmentActionLabel,
  getPriceAdjustmentSameStartNoticeText,
  getPriceAdjustmentScheduleSummary,
  getPriceAdjustmentScopeLabel,
  getPriceAdjustmentUpcomingSameStartNotice,
} from '@/lib/services/price-adjustments/priceAdjustments.summary'
import { salesOrderQueries } from '@/lib/services/sales-orders/sales-orders.query'
import { stripeConnectQueries } from '@/lib/services/stripe-connect/stripe-connect.query'
import { storeQueries } from '@/lib/services/stores/stores.query'
import { PAUSED_ORDERING_ADMIN_COPY } from '@/lib/services/stores/stores.utils'
import { formatNumber, unslugify } from '@/lib/utils'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
  AlertTriangle,
  ArrowRight,
  BellRing,
  CalendarDays,
  ChartSpline,
  ChevronRight,
  Clock3,
  Flower2,
  Globe,
  LibraryBig,
  MapPin,
  MessageSquareText,
  Palette,
  ShieldAlert,
  ShoppingCart,
  UserRoundSearch,
} from 'lucide-react'

function canRead(ability, subject) {
  return ability.can('list', subject) || ability.can('view', subject)
}

function getIndexResponseItems(response) {
  if (Array.isArray(response?.data)) {
    return response.data
  }

  if (Array.isArray(response)) {
    return response
  }

  return []
}

function getIndexResponseTotal(response) {
  const explicitTotal = Number(response?.meta?.total)

  if (Number.isFinite(explicitTotal)) {
    return explicitTotal
  }

  return getIndexResponseItems(response).length
}

const subscriptionAttentionFilters = {
  status_filter: 'needs_attention',
  sort: 'attention',
}
const dashboardNoticeClassName = 'dashboard-notice'
function getOrderAttentionTitle(order) {
  return (
    order?.recipient_name ||
    order?.ordering_contact?.name ||
    order?.purchaser?.name ||
    `Order #${order?.id}`
  )
}

function getOrderAttentionSubtitle(order) {
  const details = [`Order #${order.id}`]

  if (order?.fulfillment_due_date) {
    details.push(`Due ${dayjs(order.fulfillment_due_date).format('ddd, MMM D')}`)
  }

  if (order?.fulfillment_type) {
    details.push(unslugify(order.fulfillment_type))
  }

  return details.join(' • ')
}

function getOrderAttentionCustomer(order) {
  const title = getOrderAttentionTitle(order)
  const customerName = order?.purchaser?.name || order?.ordering_contact?.name

  if (!customerName || customerName === title) {
    return null
  }

  return customerName
}

function buildAttentionItems({ dueTodayOrders, needsFulfillmentOrders, newOrders }) {
  const seenOrderIds = new Set()
  const items = []

  const addOrders = (orders, label, variant) => {
    orders.forEach((order) => {
      if (!order?.id || seenOrderIds.has(order.id)) {
        return
      }

      if (order.fulfillment_status === SalesOrderFulfillmentStatus.Fulfilled.Value) {
        return
      }

      seenOrderIds.add(order.id)
      items.push({ label, order, variant })
    })
  }

  addOrders(dueTodayOrders, 'Due Today', 'destructive')
  addOrders(newOrders, 'New', 'info')
  addOrders(needsFulfillmentOrders, 'Needs Fulfillment', 'warning')

  return items.slice(0, 2)
}

async function loadOperationalDashboardSnapshot({
  canListMessages,
  canListSalesOrders,
  organizationId,
  organizationTimezone,
  queryClient,
}) {
  const today = organizationTimezone ? dayjs().tz(organizationTimezone) : dayjs()
  const todayDate = today.format('YYYY-MM-DD')

  const [newOrdersResponse, dueTodayResponse, needsFulfillmentResponse, unreadMessagesResponse] =
    await Promise.all([
      canListSalesOrders
        ? queryClient.ensureQueryData(
            salesOrderQueries.index({
              organizationId,
              filters: {
                created: todayDate,
                sort: 'created_desc',
                page: 1,
                page_size: 2,
              },
            })
          )
        : Promise.resolve(null),
      canListSalesOrders
        ? queryClient.ensureQueryData(
            salesOrderQueries.index({
              organizationId,
              filters: {
                due: todayDate,
                sort: 'fulfillment_due_date_asc',
                page: 1,
                page_size: 2,
              },
            })
          )
        : Promise.resolve(null),
      canListSalesOrders
        ? queryClient.ensureQueryData(
            salesOrderQueries.index({
              organizationId,
              filters: {
                fulfillment_status_filter: SalesOrderFulfillmentStatus.ToDo.Value,
                sort: 'fulfillment_due_date_asc',
                page: 1,
                page_size: 2,
              },
            })
          )
        : Promise.resolve(null),
      canListMessages
        ? queryClient.ensureQueryData(
            messageQueries.index({
              organizationId,
              filters: {
                filter: 'unread',
              },
              page: 1,
              pageSize: 1,
            })
          )
        : Promise.resolve(null),
    ])

  return {
    attentionItems: buildAttentionItems({
      dueTodayOrders: getIndexResponseItems(dueTodayResponse),
      needsFulfillmentOrders: getIndexResponseItems(needsFulfillmentResponse),
      newOrders: getIndexResponseItems(newOrdersResponse),
    }),
    metrics: {
      dueToday: getIndexResponseTotal(dueTodayResponse),
      needsFulfillment: getIndexResponseTotal(needsFulfillmentResponse),
      newOrders: getIndexResponseTotal(newOrdersResponse),
      unreadMessages: getIndexResponseTotal(unreadMessagesResponse),
    },
    todayDate,
  }
}

function DashboardStatCard({ accent, tone, icon, link, title, value }) {
  return (
    <Link {...link} className="group block h-full">
      <Card interactive accent={accent} className="h-full overflow-hidden">
        <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
          <div className="space-y-3">
            <CardDescription>{title}</CardDescription>
            <CardTitle className="text-4xl font-semibold tabular-nums">
              {formatNumber(value)}
            </CardTitle>
          </div>
          <StyledIcon icon={icon} tone={tone} />
        </CardHeader>
        <CardContent className="pt-0">
          <Typography
            variant="bodySmall"
            tone="interaction"
            interaction="link"
            className="inline-flex items-center gap-2"
          >
            Open queue
            <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
          </Typography>
        </CardContent>
      </Card>
    </Link>
  )
}

function AttentionOrderCard({ item, link }) {
  const customerName = getOrderAttentionCustomer(item.order)

  return (
    <Link {...link} className="group block">
      <Card interactive>
        <CardContent className="flex items-center justify-between gap-4 py-5">
          <div className="min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <Typography variant="emphasis" className="truncate">
                {getOrderAttentionTitle(item.order)}
              </Typography>
              <Badge variant={item.variant}>{item.label}</Badge>
            </div>
            <Typography variant="supporting" className="block truncate">
              {getOrderAttentionSubtitle(item.order)}
            </Typography>
            {customerName ? (
              <Typography variant="label" tone="muted" className="block">
                {customerName}
              </Typography>
            ) : null}
          </div>
          <ArrowRight className="size-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-interaction" />
        </CardContent>
      </Card>
    </Link>
  )
}

function DashboardSidebarCard({ items, primaryAction = null, title }) {
  if (!primaryAction && !items.length) {
    return null
  }

  return (
    <Card>
      <CardHeader>
        <Typography variant="h5">{title}</Typography>
      </CardHeader>
      <CardContent className="space-y-3">
        {primaryAction ? (
          <Link
            {...primaryAction.link}
            className="flex items-center justify-between rounded-xl bg-action px-4 py-3 text-action-foreground transition-colors hover:bg-action-hover"
          >
            <div className="flex items-center gap-3">
              <primaryAction.icon className="size-5" />
              <span className="font-medium">{primaryAction.title}</span>
            </div>
            <ArrowRight className="size-4" />
          </Link>
        ) : null}

        <div className="space-y-1.5">
          {items.map((item) => (
            <Link
              key={item.title}
              {...item.link}
              className="flex items-center justify-between gap-3 rounded-xl px-2 py-2 transition-colors hover:bg-muted/50"
            >
              <div className="flex min-w-0 items-center gap-3">
                <item.icon className="size-4 shrink-0 text-muted-foreground" />
                <span className="truncate">{item.title}</span>
              </div>
              {typeof item.badge === 'number' && item.badge > 0 ? (
                <Badge variant="secondary">{formatNumber(item.badge)}</Badge>
              ) : (
                <ArrowRight className="size-4 shrink-0 text-muted-foreground" />
              )}
            </Link>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

export const Route = createFileRoute('/_authenticated/$orgId/admin')({
  component: RouteComponent,
  loader: async ({ context, params }) => {
    const canViewDashboard =
      context.ability.can('list', 'dashboard') || context.ability.can('view', 'dashboard')
    const canListSalesOrders = context.ability.can('list', 'sales-orders')
    const canListMessages = context.ability.can('list', 'messages')
    const canListSubscriptions = context.ability.can('list', 'flower-subscriptions')
    const canManageStripeAccount = context.ability.can('edit', 'stripe-account')
    const canManageProducts =
      context.ability.can('edit', 'products') || context.ability.can('create', 'products')
    const canReadStores =
      context.ability.can('list', 'stores') || context.ability.can('view', 'stores')
    const canViewTransactions = context.ability.can('list', 'transactions')
    const isGlobalOrg = Number(params.orgId) === Number(import.meta.env.VITE_TUKIOS_FLOWERS_ORG_ID)
    const loaderData = {
      dashboardSnapshot: null,
      shouldProbeStripeAccountAlerts: false,
      store: null,
    }
    const shouldLoadOperationalDashboard =
      !isGlobalOrg && !canViewDashboard && (canListSalesOrders || canListMessages)
    let organization =
      context.queryClient.getQueryData?.(organizationQueries.show(params.orgId).queryKey) ?? null

    if ((canViewDashboard || shouldLoadOperationalDashboard) && !organization) {
      organization = await context.queryClient.ensureQueryData(
        organizationQueries.show(params.orgId)
      )
    }

    if (canReadStores) {
      loaderData.store = await context.queryClient
        .ensureQueryData(storeQueries.show({ organizationId: params.orgId }))
        .catch(() => null)
    }

    if (canManageProducts && supportsPriceAdjustmentsForOrganizationId(params.orgId)) {
      void context.queryClient
        .ensureQueryData(
          priceAdjustmentQueries.index({
            organizationId: params.orgId,
            filters: { include: ['collections'] },
          })
        )
        .catch(() => null)
    }

    if (!isGlobalOrg && canListSubscriptions) {
      void context.queryClient.prefetchQuery(
        flowerSubscriptionQueries.index({
          organizationId: params.orgId,
          filters: subscriptionAttentionFilters,
          page: 1,
          pageSize: 1,
        })
      )
    }

    if (shouldLoadOperationalDashboard) {
      loaderData.dashboardSnapshot = await loadOperationalDashboardSnapshot({
        canListMessages,
        canListSalesOrders,
        organizationId: params.orgId,
        organizationTimezone: organization?.timezone,
        queryClient: context.queryClient,
      })
    }

    if (
      supportsAvailabilityChangesForOrganizationId(params.orgId) &&
      context.ability.can('list', 'availability-changes')
    ) {
      void context.queryClient.prefetchQuery(
        availabilityChangeQueries.currentSummary({ organizationId: params.orgId })
      )
      void context.queryClient.prefetchQuery(
        availabilityChangeQueries.index({
          organizationId: params.orgId,
          filters: { past: false },
        })
      )
    }

    if (!isGlobalOrg && context.ability.can('list', 'sales-orders')) {
      void context.queryClient.prefetchQuery(
        fsnNetworkQueries.incomingOrders({ organizationId: params.orgId, filters: {} })
      )
    }

    if (!canViewDashboard) {
      return loaderData
    }

    const now = organization?.timezone ? dayjs().tz(organization.timezone) : dayjs()
    const { start_date: startDate, end_date: endDate } = getCurrentReportDateFilters(now)

    void context.queryClient.prefetchQuery(
      salesOrderQueries.analytics({
        organizationId: params.orgId,
        filters: { start_date: startDate, end_date: endDate },
      })
    )
    if (canViewTransactions && isGlobalOrg) {
      void context.queryClient.prefetchQuery(
        platformLedgerQueries.summary({
          organizationId: params.orgId,
          filters: { start_date: startDate, end_date: endDate },
        })
      )
    }
    if (organization?.sales_demo) {
      return loaderData
    }

    void context.queryClient.prefetchQuery(organizationQueries.onboarding(params?.orgId))

    const stripeConnectStatus = canManageStripeAccount
      ? await context.queryClient
          .ensureQueryData(stripeConnectQueries.status({ organizationId: params.orgId }))
          .catch(() => null)
      : null

    return {
      ...loaderData,
      shouldProbeStripeAccountAlerts:
        canManageStripeAccount && canShowStripeConnectBanner(stripeConnectStatus?.status),
    }
  },
})

export function RouteComponent() {
  const path = usePathHelper()
  const { orgId } = Route.useParams()
  const ability = useAbility()
  const { dashboardSnapshot, shouldProbeStripeAccountAlerts, store } = Route.useLoaderData()
  const { organization: currentOrganization } = useOrganizationContext()
  const isSalesDemo = Boolean(currentOrganization?.sales_demo)
  const isGlobalOrg = Number(orgId) === Number(import.meta.env.VITE_TUKIOS_FLOWERS_ORG_ID)
  const canViewDashboard = canRead(ability, 'dashboard')
  const canViewPriceChanges = ability.can('edit', 'products')
  const canReadStores = canRead(ability, 'stores')
  const canViewSalesOrders = canRead(ability, 'sales-orders')
  const canListSalesOrders = ability.can('list', 'sales-orders')
  const canListSubscriptions = ability.can('list', 'flower-subscriptions')
  const { data: wireInInbox } = useFsnIncomingOrders(
    {},
    { enabled: !isGlobalOrg && canListSalesOrders }
  )
  const unhandledWireInCount = Number(wireInInbox?.summary?.unhandled ?? 0)
  const canManageStripeAccount = ability.can('edit', 'stripe-account')
  const canCreateSalesOrders = ability.can('create', 'sales-orders')
  const canViewMessages = canRead(ability, 'messages')
  const canListMessages = ability.can('list', 'messages')
  const canViewCustomers = canRead(ability, 'customers')
  const canViewDesigns = canRead(ability, 'designs')
  const canViewProducts = canRead(ability, 'products')
  const canViewCollections = canRead(ability, 'collections')
  const canViewLocations = canRead(ability, 'locations')
  const canViewDomains = canRead(ability, 'domains')
  const canViewNotifications = canRead(ability, 'notification-recipients')
  const canManageDesigns = ability.can('edit', 'designs') || ability.can('create', 'designs')
  const canManageProducts = ability.can('edit', 'products') || ability.can('create', 'products')
  const canManageCollections =
    ability.can('edit', 'collections') || ability.can('create', 'collections')
  const canManageLocations = ability.can('edit', 'locations') || ability.can('create', 'locations')
  const canManageDomains = ability.can('edit', 'domains') || ability.can('create', 'domains')
  const canManageNotifications =
    ability.can('edit', 'notification-recipients') ||
    ability.can('create', 'notification-recipients')
  const canListAvailabilityChanges =
    supportsAvailabilityChangesForOrganizationId(orgId) &&
    ability.can('list', 'availability-changes')

  const quickActions = [
    canViewSalesOrders
      ? {
          title: 'Orders',
          link: path.salesOrders.index().link,
          icon: ShoppingCart,
        }
      : null,
    canViewSalesOrders
      ? {
          title: 'Pending Fulfillment',
          link: path.salesOrders.index().search({
            fulfillment_status_filter: SalesOrderFulfillmentStatus.ToDo.Value,
            sort: 'fulfillment_due_date_asc',
          }).link,
          icon: ShieldAlert,
          badge: canListSalesOrders ? dashboardSnapshot?.metrics?.needsFulfillment : undefined,
        }
      : null,
    canViewMessages
      ? {
          title: 'Messages',
          link: path.messages.index().search({ filter: 'unread' }).link,
          icon: MessageSquareText,
          badge: canListMessages ? dashboardSnapshot?.metrics?.unreadMessages : undefined,
        }
      : null,
    canViewCustomers
      ? {
          title: 'Customers',
          link: path.customers.index().link,
          icon: UserRoundSearch,
        }
      : null,
  ].filter(Boolean)

  const setupActions = [
    canViewProducts && (canViewDashboard || canManageProducts)
      ? {
          title: 'Products',
          link: path.products.index().link,
          icon: Flower2,
        }
      : null,
    canViewCollections && (canViewDashboard || canManageCollections)
      ? {
          title: 'Collections',
          link: path.collections.index().link,
          icon: LibraryBig,
        }
      : null,
    canViewDesigns && (canViewDashboard || canManageDesigns)
      ? {
          title: 'Website Updates',
          link: path.designs.index().link,
          icon: Palette,
        }
      : null,
    canViewLocations && (canViewDashboard || canManageLocations)
      ? {
          title: 'Locations',
          link: path.locations.index().link,
          icon: MapPin,
        }
      : null,
    canViewNotifications && (canViewDashboard || canManageNotifications)
      ? {
          title: 'Notifications',
          link: path.notifications.index().link,
          icon: BellRing,
        }
      : null,
    canViewDomains && (canViewDashboard || canManageDomains)
      ? {
          title: 'Domains',
          link: path.domains.index().link,
          icon: Globe,
        }
      : null,
  ].filter(Boolean)

  const createSalesOrderAction = canCreateSalesOrders
    ? {
        title: 'Create Order',
        link: path.salesOrders.create().link,
        icon: ShoppingCart,
      }
    : null

  const operationalDashboardCards = [
    canListSalesOrders && dashboardSnapshot
      ? {
          accent: 'periwinkle',
          tone: 'periwinkle',
          icon: ShoppingCart,
          link: path.salesOrders
            .index()
            .search({ created: dashboardSnapshot.todayDate, sort: 'created_desc' }).link,
          title: 'New Orders',
          value: dashboardSnapshot.metrics.newOrders,
        }
      : null,
    canListSalesOrders && dashboardSnapshot
      ? {
          accent: 'warning',
          tone: 'warning',
          icon: Clock3,
          link: path.salesOrders.index().search({
            due: dashboardSnapshot.todayDate,
            sort: 'fulfillment_due_date_asc',
          }).link,
          title: 'Due Today',
          value: dashboardSnapshot.metrics.dueToday,
        }
      : null,
    canListMessages && dashboardSnapshot
      ? {
          accent: 'brand',
          tone: 'brand',
          icon: MessageSquareText,
          link: path.messages.index().search({ filter: 'unread' }).link,
          title: 'Unread Messages',
          value: dashboardSnapshot.metrics.unreadMessages,
        }
      : null,
    canListSalesOrders && dashboardSnapshot
      ? {
          accent: 'brand',
          tone: 'brand',
          icon: ShieldAlert,
          link: path.salesOrders.index().search({
            fulfillment_status_filter: SalesOrderFulfillmentStatus.ToDo.Value,
            sort: 'fulfillment_due_date_asc',
          }).link,
          title: 'Needs Fulfillment',
          value: dashboardSnapshot.metrics.needsFulfillment,
        }
      : null,
  ].filter(Boolean)

  const attentionItems = canListSalesOrders ? (dashboardSnapshot?.attentionItems ?? []) : []
  const showOperationalWorkspace =
    !isGlobalOrg &&
    !canViewDashboard &&
    (operationalDashboardCards.length > 0 ||
      attentionItems.length > 0 ||
      quickActions.length > 0 ||
      setupActions.length > 0)

  return (
    <div
      className={`dashboard-workspace flex flex-col gap-y-4 px-2 py-4 sm:px-8 ${
        showOperationalWorkspace ? 'w-full max-w-none' : 'max-w-6xl'
      }`}
    >
      <div className="mb-4 flex items-center gap-3">
        <ChartSpline className="size-8 shrink-0 text-brand-amethyst" aria-hidden="true" />
        <Typography variant="h1">Dashboard</Typography>
      </div>

      <SalesDemoBanner className={dashboardNoticeClassName} />

      <IfGlobalOrg show={false}>
        {unhandledWireInCount > 0 ? (
          <InfoNotice
            title="Orders waiting"
            tone="warning"
            icon={<AlertTriangle />}
            className={dashboardNoticeClassName}
            action={
              <InfoNoticeAction {...path.wireIn.index().using({ orgId }).link}>
                Review orders
              </InfoNoticeAction>
            }
          >
            <span className="block">
              {unhandledWireInCount === 1
                ? '1 Flower Shop Network order needs to be accepted or refused.'
                : `${formatNumber(unhandledWireInCount)} Flower Shop Network orders need to be accepted or refused.`}
            </span>
          </InfoNotice>
        ) : null}
      </IfGlobalOrg>

      <IfGlobalOrg show={false}>
        <CondolencesReconnectNotice className={dashboardNoticeClassName} />
      </IfGlobalOrg>

      <IfGlobalOrg show={false}>
        {canReadStores && store?.is_active === false ? (
          <InfoNotice
            title="Online orders are paused"
            tone="danger"
            icon={<AlertTriangle />}
            className={dashboardNoticeClassName}
            action={
              <InfoNoticeAction {...path.availability.index().link}>
                Manage availability
              </InfoNoticeAction>
            }
          >
            <span className="block">{PAUSED_ORDERING_ADMIN_COPY}</span>
          </InfoNotice>
        ) : null}
      </IfGlobalOrg>

      <IfGlobalOrg show={false}>
        {canViewPriceChanges ? <ActivePriceChangesBanner /> : null}
      </IfGlobalOrg>

      <IfGlobalOrg show={false}>
        {canListSubscriptions ? <SubscriptionsAttentionBanner /> : null}
      </IfGlobalOrg>

      <IfGlobalOrg show={false}>
        {canListAvailabilityChanges ? <AvailabilityDashboardPanel /> : null}
      </IfGlobalOrg>

      {!isGlobalOrg && !showOperationalWorkspace && !canViewDashboard ? (
        <InfoNotice title="No workspace shortcuts yet" tone="neutral">
          This role does not have any common workspace destinations available from the admin home.
        </InfoNotice>
      ) : null}

      {showOperationalWorkspace ? (
        <section className="grid gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <div className="space-y-6">
            {operationalDashboardCards.length > 0 ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-4">
                {operationalDashboardCards.map((card) => (
                  <DashboardStatCard key={card.title} {...card} />
                ))}
              </div>
            ) : null}

            <section className="space-y-4">
              <Typography variant="h3">Needs Attention Now</Typography>
              {attentionItems.length > 0 ? (
                <div className="space-y-4">
                  {attentionItems.map((item) => (
                    <AttentionOrderCard
                      key={item.order.id}
                      item={item}
                      link={path.salesOrders.show(item.order.id).link}
                    />
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="py-6">
                    <Typography variant="supporting">
                      Nothing needs your attention right now.
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </section>
          </div>

          <aside className="space-y-4">
            <DashboardSidebarCard
              title="Quick Actions"
              primaryAction={createSalesOrderAction}
              items={quickActions}
            />
            <DashboardSidebarCard title="Shop Setup" items={setupActions} />
          </aside>
        </section>
      ) : null}

      {canViewDashboard ? (
        <>
          <IfGlobalOrg show={false}>
            {!isSalesDemo ? (
              canManageStripeAccount && shouldProbeStripeAccountAlerts ? (
                <div className="flex flex-col gap-4 has-[>.stripe-connect-notification-banner-collapsed]:gap-0">
                  <StripeConnectAlertsPanel
                    showSectionCard={false}
                    hideWhenNoRequiredActions
                    noActionsNeededStateMode="after-actionable"
                    panelClassName="pt-2"
                    subtleBorder
                  />
                  <AccountStatus />
                </div>
              ) : (
                <AccountStatus />
              )
            ) : null}
          </IfGlobalOrg>

          {!isGlobalOrg && canViewDesigns ? (
            <div className="empty:hidden">
              <SeasonalStatusSummary organizationId={orgId} />
            </div>
          ) : null}

          <div>
            <Analytics />
          </div>
        </>
      ) : null}
    </div>
  )
}

export function AvailabilityDashboardPanel() {
  const path = usePathHelper()
  const { data: summary } = useAvailabilityChangeCurrentSummary()
  const { data: changes = [] } = useAvailabilityChanges({ filters: { past: false } })
  const state = getAvailabilityState(summary)
  const locations = summary?.locations ?? []
  const activeLocations = locations.map((item) => item.location).filter(Boolean)
  const affectedLocationCount = locations.filter((item) => item.changes?.length > 0).length
  const hasActiveAvailabilityChanges = affectedLocationCount > 0
  const activeChanges = getActiveAvailabilityChanges(summary)
  const upcomingChanges = getUpcomingAvailabilityChangeOccurrences(
    changes,
    dayjs(),
    activeLocations
  )
  const hasUpcomingAvailabilityChanges = upcomingChanges.length > 0

  if (
    !hasActiveAvailabilityChanges &&
    !hasUpcomingAvailabilityChanges &&
    state.variant === 'success'
  ) {
    return null
  }

  const title = getAvailabilityAlertTitle({ activeChanges, upcomingChanges, state })
  const nextUpcomingLabel = upcomingChanges[0] ? formatUpcomingChange(upcomingChanges[0]) : null

  return (
    <PriceChangeAlert
      title={title}
      tone={getAvailabilityAlertTone(state)}
      icon={<CalendarDays />}
      className={dashboardNoticeClassName}
      action={
        <InfoNoticeAction {...path.availability.index().link}>
          View availability
          <ChevronRight className="size-3.5" />
        </InfoNoticeAction>
      }
    >
      <Typography variant="label" className="block">
        {getAvailabilityDashboardDescription({
          hasActiveAvailabilityChanges,
          nextUpcomingLabel,
          state,
        })}
      </Typography>
    </PriceChangeAlert>
  )
}

export function ActivePriceChangesBanner() {
  const path = usePathHelper()
  const { data: priceAdjustments = [] } = useOptionalPriceAdjustments()

  const activeAdjustments = priceAdjustments.filter((adjustment) => adjustment?.status === 'active')
  const sameStartNotice = getPriceAdjustmentUpcomingSameStartNotice(priceAdjustments)
  const sameStartDate = sameStartNotice?.starts_at ? dayjs(sameStartNotice.starts_at) : null
  const sameStartDateLabel = sameStartDate?.isValid() ? sameStartDate.format('MMM D') : null
  const sameStartText = getPriceAdjustmentSameStartNoticeText(sameStartNotice, {
    dateLabel: sameStartDateLabel,
  })

  const sameStartBanner = sameStartNotice ? (
    <PriceChangeAlert
      title="Price changes start on the same day"
      tone="warning"
      className={dashboardNoticeClassName}
      action={
        <InfoNoticeAction {...path.priceChanges.index().link}>
          View price changes
          <ChevronRight className="size-3.5" />
        </InfoNoticeAction>
      }
    >
      <Typography variant="label" className="block">
        {sameStartText}
      </Typography>
    </PriceChangeAlert>
  ) : null

  if (activeAdjustments.length === 0) {
    return sameStartBanner
  }

  if (activeAdjustments.length === 1) {
    const [adjustment] = activeAdjustments

    return (
      <>
        {sameStartBanner}
        <PriceChangeAlert
          title="Price change active right now"
          className={dashboardNoticeClassName}
          action={
            <InfoNoticeAction {...path.priceChanges.show(adjustment.id).link}>
              View active price change
              <ChevronRight className="size-3.5" />
            </InfoNoticeAction>
          }
        >
          <>
            <Typography variant="label" className="block">
              {getPriceAdjustmentActionLabel(adjustment)} for{' '}
              {getPriceAdjustmentScopeLabel(adjustment)}.
            </Typography>
            <Typography variant="label" className="block">
              {getPriceAdjustmentScheduleSummary(adjustment)}
            </Typography>
          </>
        </PriceChangeAlert>
      </>
    )
  }

  return (
    <>
      {sameStartBanner}
      <PriceChangeAlert
        title={`${formatNumber(activeAdjustments.length)} price changes are active right now`}
        className={dashboardNoticeClassName}
        action={
          <InfoNoticeAction {...path.priceChanges.index().link}>
            View price changes
            <ChevronRight className="size-3.5" />
          </InfoNoticeAction>
        }
      >
        <>
          <Typography variant="label" className="block">
            Review the current active pricing rules and their schedules.
          </Typography>
        </>
      </PriceChangeAlert>
    </>
  )
}

export function SubscriptionsAttentionBanner() {
  const path = usePathHelper()
  const { data: response } = useFlowerSubscriptions({
    filters: subscriptionAttentionFilters,
    page: 1,
    pageSize: 1,
  })
  const total = getIndexResponseTotal(response)

  if (total < 1) {
    return null
  }

  return (
    <PriceChangeAlert
      title={`${total} subscription${total === 1 ? '' : 's'} need${total === 1 ? 's' : ''} attention`}
      tone="warning"
      icon={<ShieldAlert />}
      className={dashboardNoticeClassName}
      action={
        <InfoNoticeAction
          {...path.subscriptions.index().link}
          search={subscriptionAttentionFilters}
        >
          View subscriptions
          <ChevronRight className="size-3.5" />
        </InfoNoticeAction>
      }
    >
      <Typography variant="label" className="block">
        Resolve payment issues and unavailable delivery / pickup dates so subscription orders can be
        created.
      </Typography>
    </PriceChangeAlert>
  )
}
